import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';

export const fetchContents = createAsyncThunk('content/fetchContents', async (params = {}, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/content', { params });
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Error loading content');
  }
});

export const fetchFeed = createAsyncThunk('content/fetchFeed', async (params = {}, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/content/feed', { params });
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Error loading feed');
  }
});

export const fetchContentById = createAsyncThunk('content/fetchContentById', async (id, { rejectWithValue }) => {
  try {
    const { data } = await api.get(`/content/${id}`);
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Content not found');
  }
});

export const fetchMyContent = createAsyncThunk('content/fetchMyContent', async (_, { rejectWithValue }) => {
  try {
    const { data } = await api.get('/creator/content');
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Error loading my content');
  }
});

export const createContent = createAsyncThunk('content/createContent', async (formData, { rejectWithValue }) => {
  try {
    const { data } = await api.post('/content', formData, {
      headers: { 'Content-Type': undefined },
    });
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Error creating content');
  }
});

export const uploadContent = createAsyncThunk('content/uploadContent', async (formData, { dispatch, rejectWithValue }) => {
  try {
    const token = localStorage.getItem('accessToken');
    const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    return await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${baseUrl}/content`);
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          dispatch(setUploadProgress(pct));
          if (pct >= 100) {
            dispatch(setUploadStage('processing'));
          }
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          dispatch(setUploadStage('done'));
          resolve(JSON.parse(xhr.responseText));
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            reject(err.error || 'Error uploading content');
          } catch {
            reject('Error uploading content');
          }
        }
      };
      xhr.onerror = () => reject('Connection error');
      xhr.send(formData);
    });
  } catch (err) {
    return rejectWithValue(typeof err === 'string' ? err : err.message || 'Error uploading content');
  }
});

export const publishContent = createAsyncThunk('content/publishContent', async (id, { rejectWithValue }) => {
  try {
    const { data } = await api.put(`/content/${id}/publish`);
    return data;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Error publishing content');
  }
});

export const deleteContent = createAsyncThunk('content/deleteContent', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`/content/${id}`);
    return id;
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Error deleting content');
  }
});

export const recordView = createAsyncThunk('content/recordView', async (id, { rejectWithValue }) => {
  try {
    const { data } = await api.post(`/content/${id}/view`);
    return { id: parseInt(id), viewCount: data.viewCount };
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Error recording view');
  }
});

export const toggleLike = createAsyncThunk('content/toggleLike', async (id, { rejectWithValue }) => {
  try {
    const { data } = await api.post(`/content/${id}/like`);
    return { id: parseInt(id), liked: data.liked, likeCount: data.likeCount };
  } catch (err) {
    return rejectWithValue(err.response?.data?.error || 'Error processing like');
  }
});

const contentSlice = createSlice({
  name: 'content',
  initialState: {
    items: [],
    feedItems: [],
    feedLoading: false,
    current: null,
    myContent: [],
    loading: false,
    error: null,
    uploading: false,
    uploadProgress: 0
  },
  reducers: {
    clearCurrent(state) { state.current = null; },
    clearError(state) { state.error = null; },
    setUploadProgress(state, action) { state.uploadProgress = action.payload; },
    setUploadStage(state, action) { state.uploadStage = action.payload; },
    resetUpload(state) { state.uploading = false; state.uploadProgress = 0; state.uploadStage = null; }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchContents.pending, (state) => { state.loading = true; })
      .addCase(fetchContents.fulfilled, (state, action) => { state.loading = false; state.items = action.payload; })
      .addCase(fetchContents.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchFeed.pending, (state) => { state.feedLoading = true; })
      .addCase(fetchFeed.fulfilled, (state, action) => { state.feedLoading = false; state.feedItems = action.payload; })
      .addCase(fetchFeed.rejected, (state) => { state.feedLoading = false; })
      .addCase(fetchContentById.pending, (state) => { state.loading = true; })
      .addCase(fetchContentById.fulfilled, (state, action) => { state.loading = false; state.current = action.payload; })
      .addCase(fetchContentById.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(fetchMyContent.pending, (state) => { state.loading = true; })
      .addCase(fetchMyContent.fulfilled, (state, action) => { state.loading = false; state.myContent = action.payload; })
      .addCase(fetchMyContent.rejected, (state, action) => { state.loading = false; state.error = action.payload; })
      .addCase(createContent.fulfilled, (state, action) => { state.myContent.push(action.payload); })
      .addCase(uploadContent.pending, (state) => { state.uploading = true; state.uploadProgress = 0; state.uploadStage = 'uploading'; state.error = null; })
      .addCase(uploadContent.fulfilled, (state, action) => { state.uploading = false; state.uploadProgress = 100; state.uploadStage = null; state.myContent.push(action.payload); })
      .addCase(uploadContent.rejected, (state, action) => { state.uploading = false; state.uploadProgress = 0; state.uploadStage = null; state.error = action.payload; })
      .addCase(publishContent.fulfilled, (state, action) => {
        const idx = state.myContent.findIndex(c => c.id === action.payload.id);
        if (idx !== -1) state.myContent[idx] = action.payload;
      })
      .addCase(deleteContent.fulfilled, (state, action) => {
        state.myContent = state.myContent.filter(c => c.id !== action.payload);
      })
      .addCase(recordView.fulfilled, (state, action) => {
        if (state.current && state.current.id === action.payload.id) {
          state.current.viewCount = action.payload.viewCount;
        }
      })
      .addCase(toggleLike.fulfilled, (state, action) => {
        if (state.current && state.current.id === action.payload.id) {
          state.current.hasLiked = action.payload.liked;
          state.current.likeCount = action.payload.likeCount;
        }
      });
  }
});

export const { clearCurrent, clearError, setUploadProgress, setUploadStage, resetUpload } = contentSlice.actions;
export default contentSlice.reducer;
